import { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "sg_cookies_consent_v1";

/**
 * Lightweight GDPR-style cookie banner.
 *
 * State stored in localStorage:
 *   { status: "accepted" | "rejected", date: ISOString }
 *
 * The site does not run any third-party analytics or marketing tags today,
 * so this banner is informative + legally compliant. If Silvia adds Google
 * Analytics / Meta Pixel in the future, gate them behind:
 *   localStorage.getItem("sg_cookies_consent_v1")?.includes("accepted").
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                // Slight delay so banner doesn't fight the initial fade-in.
                const t = setTimeout(() => setVisible(true), 900);
                return () => clearTimeout(t);
            }
        } catch {
            setVisible(true);
        }
    }, []);

    const persist = (status) => {
        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ status, date: new Date().toISOString() }),
            );
        } catch {
            /* ignore quota / private mode */
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Aviso de cookies"
            data-testid="cookie-banner"
            className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none animate-fadein"
        >
            <div className="pointer-events-auto mx-auto max-w-3xl bg-[#1C1A18] text-[#FAFAF8] rounded-2xl shadow-2xl border border-[#C4A882]/20 overflow-hidden">
                <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-start gap-4 sm:gap-5">
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#C4A882]/15 inline-flex items-center justify-center">
                        <Cookie
                            size={18}
                            strokeWidth={1.5}
                            className="text-[#C4A882]"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                            Cookies
                        </p>
                        <h3 className="font-display text-lg sm:text-xl mt-1 leading-tight">
                            Cuidamos tu privacidad
                        </h3>
                        <p className="mt-2 text-[13px] sm:text-sm font-light text-[#FAFAF8]/80 leading-relaxed">
                            Usamos cookies propias necesarias para el
                            funcionamiento de la web y, con tu permiso, cookies
                            opcionales para mejorar tu experiencia. Puedes
                            aceptarlas o rechazarlas en cualquier momento.
                        </p>
                        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={() => persist("accepted")}
                                data-testid="cookie-accept"
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#C4A882] text-[#1C1A18] text-[10px] uppercase tracking-[0.28em] hover:bg-[#d8bf94] active:scale-95 transition-all"
                            >
                                Aceptar todas
                            </button>
                            <button
                                type="button"
                                onClick={() => persist("rejected")}
                                data-testid="cookie-reject"
                                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-[#FAFAF8]/30 text-[#FAFAF8] text-[10px] uppercase tracking-[0.28em] hover:bg-[#FAFAF8] hover:text-[#1C1A18] active:scale-95 transition-all"
                            >
                                Solo necesarias
                            </button>
                            <a
                                href="/privacidad"
                                className="inline-flex items-center justify-center px-2 py-2.5 text-[10px] uppercase tracking-[0.28em] text-[#FAFAF8]/60 hover:text-[#C4A882] transition-colors"
                                data-testid="cookie-policy-link"
                            >
                                Política de privacidad
                            </a>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => persist("rejected")}
                        aria-label="Cerrar y rechazar opcionales"
                        data-testid="cookie-close"
                        className="absolute top-3 right-3 sm:static w-8 h-8 inline-flex items-center justify-center rounded-full text-[#FAFAF8]/60 hover:text-[#FAFAF8] hover:bg-[#FAFAF8]/10 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}
